import { PaginationResult } from '@/models/pagination.model';
import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from './base.service';
import { EventItem } from '@/models/event-item.model';
import { FilterData } from './users.service';

type ServerEventItem = EventItem & { languages: string };

class EventsService extends BaseService {
  private url: string = '/api/event-item';

  constructor() {
    super();
  }

  public async create(item: EventItem): Promise<DataResponse<EventItem>> {
    const res = await this.$http.post<EventItem>(this.url, item);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
    };
  }

  public async update(item: EventItem): Promise<DataResponse<EventItem>> {
    const res = await this.$http.put<EventItem>(`${this.url}/${item.id}`, item);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async publish(
    id: number,
    published: boolean,
  ): Promise<DataResponse<EventItem>> {
    const res = await this.$http.put<EventItem>(`${this.url}/publish`, {
      id,
      published,
    });

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async dismiss(
    id: number,
    dismissed: boolean,
  ): Promise<DataResponse<EventItem>> {
    const res = await this.$http.put<EventItem>(`${this.url}/dismiss`, {
      id,
      dismissed,
    });

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async get(id: number): Promise<DataResponse<EventItem>> {
    const res = await this.$http.get<ServerEventItem>(`${this.url}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getList(): Promise<DataResponse<EventItem[]>> {
    const res = await this.$http.get<ServerEventItem[]>(`${this.url}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getListPaginated(
    filters: FilterData,
  ): Promise<DataResponse<PaginationResult<EventItem[]>>> {
    let query = `?page=${filters.page}&count=${filters.count}`;

    if (filters.search) {
      query = `${query}&search=${filters.search}`;
    }

    const res = await this.$http.get<PaginationResult<EventItem[]>>(
      `${this.url}/paginated${query}`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async upload(csv: File): Promise<DataResponse<EventItem[]>> {
    const data = new FormData();
    data.append('file', csv);

    const res = await this.$http.post<EventItem[]>(
      `${this.url}/upload`,
      data,
      true,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async search(
    search: string,
    page: number = 0,
    categoryId?: number,
    locationId?: number,
  ): Promise<PaginationResult<EventItem>> {
    const res = await this.$http.get<PaginationResult<EventItem>>(
      `${this.url}/paginated?search=${search}${
        categoryId ? `&categories=${categoryId}` : ''
      }${locationId ? `&locations=${locationId}` : ''}&page=${page}&count=10`,
    );

    return res.data;
  }

  public async trigger(update?: boolean): Promise<{
    failed: number;
    finishedAt: string;
    ok: number;
    results: [];
    startedAt: string;
    totalCost: number;
    totalInserted: number;
    totalSources: number;
  }> {
    const result = await this.$http.post<{
      failed: number;
      finishedAt: string;
      ok: number;
      results: [];
      startedAt: string;
      totalCost: number;
      totalInserted: number;
      totalSources: number;
    }>(`/api/dispatcher/run-from-cms`, { update });

    return result.data;
  }
}

export default new EventsService();
